import React from "react";
import { Route, RouteProps, Switch } from "react-router-dom";
import Error500 from "./elements/errors/Error500";
import { PrivateRoute, PublicRoute } from "./helpers/route";
import { isAutomationAppConfigured } from "./pages/mobile/automationAppConfig";

const Login = React.lazy(() => import("./pages/login/Login"));

const mobileAccountCallbackRoutes: Record<string, RouteProps["render"]> = {};
if (__AUTOMATION_APP_BUILD__ && isAutomationAppConfigured()) {
  const MobileAccountCallback = React.lazy(
    () => import("./pages/mobile/MobileAccountCallback"),
  );
  mobileAccountCallbackRoutes["/mobile/account/callback"] = () => (
    <MobileAccountCallback />
  );
  const MobilePaymentCallback = React.lazy(
    () => import("./pages/mobile/MobilePaymentCallback"),
  );
  mobileAccountCallbackRoutes["/mobile/payment/callback"] = () => (
    <MobilePaymentCallback />
  );
}

const Dashboard = React.lazy(() => import("./pages/dashboard/OnBoarding"));

const Orders = React.lazy(() => import("./pages/settings/orders/Orders"));

const Comments = React.lazy(() => import("./pages/comments/Comments"));

const Post = React.lazy(() => import("./pages/post/Post"));

const Posts = React.lazy(() => import("./pages/posts/Posts"));

const Draft = React.lazy(() => import("./pages/posts/Draft"));

const Calendar = React.lazy(() => import("./pages/calendar/Calendar"));

const Account = React.lazy(() => import("./pages/settings/account/Account"));

const Accounts = React.lazy(() => import("./pages/settings/accounts/Accounts"));

const Ticket = React.lazy(() => import("./pages/settings/tickets/Ticket"));

const Tickets = React.lazy(() => import("./pages/settings/tickets/Tickets"));

const Inbox = React.lazy(() => import("./pages/inbox/Inbox"));

const Team = React.lazy(() => import("./pages/settings/teams/Team"));

const Teams = React.lazy(() => import("./pages/settings/teams/Teams"));
const TeamStatistics = React.lazy(
  () => import("./pages/settings/teams/Statistics"),
);

const User = React.lazy(() => import("./pages/settings/user/User"));

const Affiliate = React.lazy(() => import("./pages/settings/Affiliate"));

const Renew = React.lazy(() => import("./pages/renew/Renew"));

const OauthOnBoard = React.lazy(() => import("@app/pages/clients/Onboard"));
const Clients = React.lazy(() => import("@app/pages/clients/Clients"));
const Client = React.lazy(() => import("@app/pages/clients/Client"));

const Checkout = React.lazy(() => import("./pages/renew/Checkout"));

const Authorize = React.lazy(() => import("./pages/Authorize"));

const Integrations = React.lazy(
  () => import("./pages/settings/integrations/Integrations"),
);

const Settings = React.lazy(() => import("./pages/settings/Settings"));

const Automation = React.lazy(() => import("./pages/automation/Automation"));

const AutomationMenu = React.lazy(() => import("./pages/automation/Menu"));

const AutomationQuestion = React.lazy(
  () => import("./pages/automation/Question"),
);

const Product = React.lazy(() => import("./pages/automation/Product"));

const Products = React.lazy(() => import("./pages/automation/Products"));

const Response = React.lazy(() => import("./pages/automation/Response"));

const Responses = React.lazy(() => import("./pages/automation/Responses"));

const ResponseSettings = React.lazy(
  () => import("./pages/automation/ResponseSettings"),
);

const Form = React.lazy(() => import("./pages/automation/Form"));

const Forms = React.lazy(() => import("./pages/automation/Forms"));

const FormResponses = React.lazy(
  () => import("./pages/automation/FormResponses"),
);

const Leeds = React.lazy(() => import("./pages/automation/Leeds"));

export const publicApps: {
  [key: string]: RouteProps["render"];
} = {
  ...mobileAccountCallbackRoutes,
  "/:locale(en|fa)": () => <Login />,
  "/join/:affiliateId": () => (
    <PublicRoute>
      <Login />
    </PublicRoute>
  ),
  "/:page(signup)/jaryan/:jaryan": () => (
    <PublicRoute>
      <Login />
    </PublicRoute>
  ),
  "/:page(signup|login)/:implicit(implicit)": () => (
    <PublicRoute>
      <Login />
    </PublicRoute>
  ),
  "/login/otp": () => (
    <PublicRoute>
      <Login />
    </PublicRoute>
  ),
  "/:page(login|signup|recovery)/:hash?": () => (
    <PublicRoute>
      <Login />
    </PublicRoute>
  ),
  "/:page(complete|verify|verify-email)": () => (
    <PrivateRoute verify={false}>
      <Login />
    </PrivateRoute>
  ),
  "/authorize": () => (
    <PrivateRoute>
      <Authorize />
    </PrivateRoute>
  ),
};

export const apps: {
  [key: string]: RouteProps["render"];
} = {
  "/dashboard": () => (
    <PrivateRoute>
      <Dashboard />
    </PrivateRoute>
  ),
  "/order": () => (
    <PrivateRoute>
      <Orders />
    </PrivateRoute>
  ),
  "/comment/:commentId?": () => (
    <PrivateRoute requiresAccount>
      <Comments />
    </PrivateRoute>
  ),
  "/comments/:commentId?": () => (
    <PrivateRoute requiresAccount>
      <Comments />
    </PrivateRoute>
  ),
  "/:type(post|story)/:postId?": () => (
    <PrivateRoute requiresAccount>
      <Post />
    </PrivateRoute>
  ),
  "/posts": () => (
    <PrivateRoute requiresAccount>
      <Posts />
    </PrivateRoute>
  ),
  "/draft": () => (
    <PrivateRoute requiresAccount>
      <Draft />
    </PrivateRoute>
  ),
  "/schedule-calendar": () => (
    <PrivateRoute requiresAccount>
      <Calendar />
    </PrivateRoute>
  ),
  "/accounts/:social?/new": () => (
    <PrivateRoute>
      <Account />
    </PrivateRoute>
  ),
  "/accounts/:accountId/:relogin(relogin)?": () => (
    <PrivateRoute>
      <Account />
    </PrivateRoute>
  ),
  "/accounts": () => (
    <PrivateRoute>
      <Accounts />
    </PrivateRoute>
  ),
  "/ticket/:ticketId": () => (
    <PrivateRoute>
      <Ticket />
    </PrivateRoute>
  ),
  "/ticket": () => (
    <PrivateRoute>
      <Tickets />
    </PrivateRoute>
  ),
  "/inbox/:conversationId?": () => (
    <PrivateRoute requiresAccount>
      <Inbox />
    </PrivateRoute>
  ),
  "/team/:teamId/statistics": () => (
    <PrivateRoute>
      <TeamStatistics />
    </PrivateRoute>
  ),
  "/team/:teamId": () => (
    <PrivateRoute>
      <Team />
    </PrivateRoute>
  ),
  "/team": () => (
    <PrivateRoute>
      <Teams />
    </PrivateRoute>
  ),
  "/user/:section?": () => (
    <PrivateRoute>
      <User />
    </PrivateRoute>
  ),
  "/settings/affiliate": () => (
    <PrivateRoute>
      <Affiliate />
    </PrivateRoute>
  ),
  "/renew/:packageId?": () => (
    <PrivateRoute>
      <Renew />
    </PrivateRoute>
  ),
  "/checkout": () => (
    <PrivateRoute>
      <Checkout />
    </PrivateRoute>
  ),
  "/integrations/:integrationId": () => (
    <PrivateRoute>
      <Account />
    </PrivateRoute>
  ),
  "/integrations": () => (
    <PrivateRoute>
      <Integrations />
    </PrivateRoute>
  ),
  "/settings": () => (
    <PrivateRoute>
      <Settings />
    </PrivateRoute>
  ),
  "/clients/new": () => (
    <PrivateRoute>
      <Client />
    </PrivateRoute>
  ),
  "/clients/onboard": () => (
    <PrivateRoute>
      <OauthOnBoard />
    </PrivateRoute>
  ),
  "/clients/:clientId": () => (
    <PrivateRoute>
      <Client />
    </PrivateRoute>
  ),
  "/clients": () => (
    <PrivateRoute>
      <Clients />
    </PrivateRoute>
  ),
  "/automation-accounts": () => (
    <PrivateRoute requiresAccount>
      <Automation />
    </PrivateRoute>
  ),
  "/automation/:accountId?": () => (
    <PrivateRoute requiresAccount>
      <Switch>
        <Route exact path="/automation">
          <Automation />
        </Route>
        <Route path="/automation/:accountId/question">
          <AutomationQuestion />
        </Route>
        <Route path="/automation/:accountId/product/:productId">
          <Product />
        </Route>
        <Route path="/automation/:accountId/product">
          <Products />
        </Route>
        <Route path="/automation/:accountId/response-settings">
          <ResponseSettings />
        </Route>
        <Route path="/automation/:accountId/response/:responseId">
          <Response />
        </Route>
        <Route path="/automation/:accountId/response">
          <Responses />
        </Route>
        <Route path="/automation/:accountId/form/:formId/responses">
          <FormResponses />
        </Route>
        <Route path="/automation/:accountId/form/:formId">
          <Form />
        </Route>
        <Route path="/automation/:accountId/form">
          <Forms />
        </Route>
        <Route path="/automation/:accountId/leed">
          <Leeds />
        </Route>
        <Route path="/automation/:accountId/menu">
          <AutomationMenu />
        </Route>
      </Switch>
    </PrivateRoute>
  ),
};

const Error404 = React.lazy(() => import("./elements/errors/Error404"));

export const errors = {
  "404": Error404,
  "500": Error500,
};
